import { useMutation } from '@src/query';


/**
 * Canned-response mutation hooks (#268). Each wraps the shared `useMutation`
 * engine with the injected-`queryClient` `mutateFn({ apiClient, queryClient })`
 * contract — the corelib query singleton is NOT imported here. Invalidation runs
 * inside `mutateFn` (the one place `queryClient` is injected) so the exact cache
 * key prefix for the list query is preserved.
 *
 * The list is read with `useQuery('/leads/canned-responses/<sellerId>/')`, whose
 * key is derived as `[namespace, ...url.split('/')]` (see
 * `corelib/query/useBaseQuery.js` + `urlToQueryKey`). That yields
 * `['root','','leads','canned-responses',sellerId,'']`; the prefix below
 * `['root','','leads','canned-responses',sellerId]` matches it (TanStack matches
 * a key by any leading prefix). Mirrors the Leads/Customers mutation modules.
 */

/** Cache key prefix for a seller's canned-responses list. */
function cannedResponsesKeyPrefix(sellerId) {
  return ['root', '', 'leads', 'canned-responses', String(sellerId || '')];
}


/** Create a canned response. Variables: `{ sellerId, title, body }`. */
function useCreateCannedResponseMutation(params) {
  return useMutation({
    mutateFn: async ({ apiClient, queryClient, sellerId, title, body }) => {
      const data = await apiClient.request({
        url: `/leads/canned-responses/${sellerId}/`,
        method: 'POST',
        payload: { title, body },
      });
      queryClient.invalidateQueries({ queryKey: cannedResponsesKeyPrefix(sellerId) });
      return data;
    },
    ...params,
  });
}


/** Update a canned response. Variables: `{ sellerId, id, ...fields }` where
 * `fields` is `title` and/or `body`. */
function useUpdateCannedResponseMutation(params) {
  return useMutation({
    mutateFn: async ({ apiClient, queryClient, sellerId, id, ...fields }) => {
      const data = await apiClient.request({
        url: `/leads/canned-responses/${sellerId}/${id}/`,
        method: 'PATCH',
        payload: fields,
      });
      queryClient.invalidateQueries({ queryKey: cannedResponsesKeyPrefix(sellerId) });
      return data;
    },
    ...params,
  });
}


/** Delete a canned response. Variables: `{ sellerId, id }`. */
function useDeleteCannedResponseMutation(params) {
  return useMutation({
    mutateFn: async ({ apiClient, queryClient, sellerId, id }) => {
      const data = await apiClient.request({
        url: `/leads/canned-responses/${sellerId}/${id}/`,
        method: 'DELETE',
      });
      queryClient.invalidateQueries({ queryKey: cannedResponsesKeyPrefix(sellerId) });
      return data;
    },
    ...params,
  });
}


/** Reorder canned responses. Variables: `{ sellerId, orderedIds }` → POST with
 * `{ ordered_ids }`. */
function useReorderCannedResponseMutation(params) {
  return useMutation({
    mutateFn: async ({ apiClient, queryClient, sellerId, orderedIds }) => {
      const data = await apiClient.request({
        url: `/leads/canned-responses/${sellerId}/reorder/`,
        method: 'POST',
        payload: { ordered_ids: orderedIds },
      });
      queryClient.invalidateQueries({ queryKey: cannedResponsesKeyPrefix(sellerId) });
      return data;
    },
    ...params,
  });
}


export {
  cannedResponsesKeyPrefix,
  useCreateCannedResponseMutation,
  useDeleteCannedResponseMutation,
  useReorderCannedResponseMutation,
  useUpdateCannedResponseMutation,
};
